import {
    AenApplicationSidebar,
    AenApplicationSidebarNavItem,
} from "layouts/admin/aen-application-layout/aen-application-sidebar/aen-application-sidebar";
import { ButtonIcon } from "components/buttons/button-icon/button-icon";
import { ButtonStyle } from "components/buttons/button/button";
import { EditInstructorApplicationModal } from "pages/admin/aen-applications/edit-instructor-application-modal/edit-instructor-application-modal";
import { HeaderBanner } from "components/header-banner/header-banner";
import { Icons } from "components/icons/constants/icons";
import { InstructorApplicationContext } from "utilities/contexts/use-instructor-application-context";
import { Outlet } from "react-router";
import { RoleType } from "models/enumerations/users/role-type";
import { SideContentLeftLayout } from "components/layouts/side-content-left-layout/side-content-left-layout";
import { SkipNavContent } from "@chakra-ui/skip-nav";
import { sitemap } from "sitemap";
import { t } from "utilities/localization/t";
import { useAenInstructorApplication } from "utilities/hooks/models/aen-applications/use-aen-instructor-application";
import { useEffect, useMemo, useState } from "react";
import { useGlobalState } from "utilities/contexts/use-global-state-context";
import "./aen-application-layout.scss";

// -----------------------------------------------------------------------------------------
// #region Interfaces
// -----------------------------------------------------------------------------------------

interface AenInstructorApplicationLayoutProps {}

// #endregion Interfaces

// -----------------------------------------------------------------------------------------
// #region Constants
// -----------------------------------------------------------------------------------------

const CSS_CLASS_NAME = "aen-application-layout";

// #endregion Constants

// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const AenInstructorApplicationLayout: React.FC<AenInstructorApplicationLayoutProps> = () => {
    const { record: globalState } = useGlobalState();
    const isNfpaAdmin = globalState?.currentIdentity?.isCurrentlyInRole(RoleType.NfpaAdministrator);

    const { instructorApplication, setInstructorApplication, saveApplication } =
        useAenInstructorApplication({});
    const [showEditModal, setShowEditModal] = useState<boolean>(false);

    const navItems = useMemo(() => {
        const items: AenApplicationSidebarNavItem[] = [
            {
                path: sitemap.admin.aenApplications.instructors.detail,
                text: t("applicationDetails"),
            },
            {
                path: sitemap.admin.aenApplications.instructors.files,
                text: t("files"),
            },
            {
                path: sitemap.admin.aenApplications.instructors.logs,
                text: t("activityLogs"),
            },
        ];
        return items;
    }, []);

    useEffect(() => {
        if (isNfpaAdmin) {
            navItems.splice(2, 0, {
                path: sitemap.admin.aenApplications.instructors.assessments,
                text: t("assessments"),
            });
        }
    }, [isNfpaAdmin, navItems]);

    return (
        <InstructorApplicationContext.Provider
            value={{ record: instructorApplication, setRecord: setInstructorApplication }}>
            <section className={CSS_CLASS_NAME}>
                <HeaderBanner
                    title={
                        `${instructorApplication?.firstName} ${instructorApplication?.lastName}` ??
                        ""
                    }>
                    <ButtonIcon
                        onClick={() => {
                            setShowEditModal(true);
                        }}
                        buttonStyle={ButtonStyle.Secondary}
                        text={t("updateStatus")}
                        iconType={Icons.Edit}
                    />
                </HeaderBanner>
                <SideContentLeftLayout
                    sidebarElement={<AenApplicationSidebar navItems={navItems} />}>
                    <div className="content-wrap">
                        <div className="content">
                            <SkipNavContent>
                                <Outlet />
                            </SkipNavContent>
                        </div>
                    </div>
                </SideContentLeftLayout>
            </section>
            {instructorApplication != null && (
                <EditInstructorApplicationModal
                    open={showEditModal}
                    setOpen={setShowEditModal}
                    instructorApplicationToEdit={instructorApplication}
                    onSave={saveApplication}
                />
            )}
        </InstructorApplicationContext.Provider>
    );
};

// #endregion Component

// -----------------------------------------------------------------------------------------
// #region Exports
// -----------------------------------------------------------------------------------------

export { AenInstructorApplicationLayout };

// #endregion Exports
